import React from "react";
import { CheckCircle } from "lucide-react";

interface DifficultyStats {
  easy: number;
  medium: number;
  hard: number;
}

interface DifficultyStatsBarProps {
  stats: DifficultyStats;
}

const DifficultyStatsBar: React.FC<DifficultyStatsBarProps> = ({ stats }) => {
  const total = stats.easy + stats.medium + stats.hard;

  const levels = [
    { key: "easy" as const, label: "Easy", color: "emerald", bg: "bg-emerald-500" },
    { key: "medium" as const, label: "Medium", color: "amber", bg: "bg-amber-500" },
    { key: "hard" as const, label: "Hard", color: "red", bg: "bg-red-500" },
  ];

  return (
    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-white">Problems Solved</span>
        <div className="flex items-center gap-1 text-emerald-400">
          <CheckCircle className="w-4 h-4" />
          <span className="text-lg font-bold">{total}</span>
        </div>
      </div>

      {/* Progress Bars */}
      <div className="space-y-3">
        {levels.map((level) => {
          const count = stats[level.key];
          const percentage = total > 0 ? (count / total) * 100 : 0;
          
          return (
            <div key={level.key}>
              <div className="flex justify-between items-center mb-1">
                <span className={`text-sm font-medium text-${level.color}-400`}>
                  {level.label}
                </span>
                <span className="text-sm text-gray-400">{count}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full ${level.bg} rounded-full transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats */}
      {total > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-3 gap-2 text-center">
          {levels.map((level) => {
            const count = stats[level.key];
            const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={level.key}>
                <div className={`text-lg font-bold text-${level.color}-400`}>
                  {percentage}%
                </div>
                <div className="text-xs text-gray-500">{level.label}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DifficultyStatsBar;